/**
 * Demo Data Seeder & Cleaner
 * All demo documents are tagged with { _demo: true } for safe selective deletion.
 */
import {
  collection, addDoc, setDoc, doc, getDocs, deleteDoc, query, where
} from "firebase/firestore";

const NOW = Date.now();
const DAY = 86400000;

// ── Demo Episodes ────────────────────────────────────────────────────────────
const DEMO_EPISODES = [
  {
    title: "The Truth About Police Reform",
    description: "Kedrick sits down with former NYPD officer Marcus Johnson to break down what real police reform looks like from inside the badge — beyond the headlines and the politics.",
    guestName: "Marcus Johnson",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    spotifyUrl: "https://open.spotify.com/episode/demo1",
    date: "2024-03-15",
    topic: "Law Enforcement",
    createdAt: NOW - 6 * DAY,
  },
  {
    title: "Veterans and the Mental Health Crisis",
    description: "One in four veterans struggles with PTSD and depression after returning home. Combat medic Sarah Williams shares what the VA isn't telling you — and what's actually working.",
    guestName: "Sarah Williams",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    spotifyUrl: "https://open.spotify.com/episode/demo2",
    date: "2024-03-08",
    topic: "Veterans Affairs",
    createdAt: NOW - 13 * DAY,
  },
  {
    title: "Border Security: A Reality Check",
    description: "Border Patrol Agent Carlos Rivera has spent 18 years on the front lines. He tells us what the cameras don't show, what both sides of the aisle get wrong, and what actually needs to change.",
    guestName: "Carlos Rivera",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    spotifyUrl: "https://open.spotify.com/episode/demo3",
    date: "2024-02-29",
    topic: "National Security",
    createdAt: NOW - 20 * DAY,
  },
  {
    title: "Inside the Justice System",
    description: "Retired judge Patricia Moore spent 22 years on the bench. She pulls no punches on mandatory minimums, prosecutorial overreach, and why justice is not always blind.",
    guestName: "Patricia Moore",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    spotifyUrl: "https://open.spotify.com/episode/demo4",
    date: "2024-02-22",
    topic: "Criminal Justice",
    createdAt: NOW - 27 * DAY,
  },
  {
    title: "Community Policing: Does It Actually Work?",
    description: "Chief David Harris rebuilt trust in his city after years of tension between residents and law enforcement. His blueprint might be the model every city needs.",
    guestName: "David Harris",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    spotifyUrl: "https://open.spotify.com/episode/demo5",
    date: "2024-02-15",
    topic: "Community & Policing",
    createdAt: NOW - 34 * DAY,
  },
];

// ── Demo Blog Posts ──────────────────────────────────────────────────────────
const DEMO_POSTS = [
  {
    title: "Why Police Reform Needs to Start From Within",
    excerpt: "Everyone has an opinion about policing. Few have the lived experience to back it up. Kedrick breaks down why meaningful reform has to come from officers themselves.",
    body: "<p>The debate around police reform has never been louder. But if you listen carefully, you'll notice something: the loudest voices often have the least time on the ground. Real reform — the kind that sticks — has to be built from the inside out.</p><p>I spent years in law enforcement. I've seen what breaks departments and what builds them. The answer isn't defunding. It's not militarizing. It's accountability, leadership, and a culture that rewards truth over loyalty to the badge.</p><h2>The Culture Problem</h2><p>Every department has what insiders call the 'blue wall' — the unspoken code that you protect your own. When that code overrides ethics, you get the incidents that make national news. Fixing the culture means leadership that refuses to tolerate silence in the face of misconduct.</p><h2>Training Matters More Than Equipment</h2><p>Departments spend millions on vehicles and tactical gear. They spend far less on mental health training, de-escalation, and community engagement skills. That imbalance shows in outcomes.</p><p>The path forward isn't simple. But it starts with honest conversations — exactly the kind we have on this show.</p>",
    coverUrl: "https://picsum.photos/seed/policereform/800/450",
    status: "published",
    date: "2024-03-16",
    createdAt: NOW - 5 * DAY,
  },
  {
    title: "The Veteran Crisis Nobody Talks About",
    excerpt: "22 veterans take their own lives every single day. We talk about supporting the troops — but what does that mean once they come home? Kedrick weighs in.",
    body: "<p>We say 'thank you for your service' like it's a transaction. You served, I thanked you, we're square. But service doesn't end at the discharge date. For many veterans, that's when the hardest battle begins.</p><p>22 veterans die by suicide every day in America. That number has barely moved in a decade despite billions spent on VA programs. Why? Because we keep treating the symptom rather than the wound.</p><h2>The Transition Gap</h2><p>Going from a highly structured military environment to civilian life is a jarring shock that few outsiders understand. Your rank, your mission, your identity — gone overnight. Many veterans describe it as free-falling with no parachute.</p><h2>What Actually Helps</h2><p>The programs showing the most success aren't the massive institutional ones. They're peer-to-peer networks, veteran-run nonprofits, and mentorship programs that pair newly discharged service members with veterans who've successfully made the transition.</p><p>We can do better. We owe them that much.</p>",
    coverUrl: "https://picsum.photos/seed/veterans/800/450",
    status: "published",
    date: "2024-03-09",
    createdAt: NOW - 12 * DAY,
  },
  {
    title: "What the Media Gets Wrong About Law Enforcement",
    excerpt: "From body camera footage to use-of-force statistics, the gap between how law enforcement is reported and how it actually operates is wider than most people realize.",
    body: "<p>I've been on both sides — behind the badge and behind the microphone. And I can tell you with certainty: the way law enforcement is covered in the media bears little resemblance to the day-to-day reality of policing in America.</p><p>That's not a critique of journalists. It's a structural problem. Breaking news rewards the dramatic. The routine — the 99% of interactions that end without incident — is invisible.</p><h2>The Context Problem</h2><p>When a use-of-force incident goes viral, viewers see seconds of footage stripped of context. What led up to it? What did the officer know? What were the threats? That context rarely makes it into the headline.</p><h2>The Numbers Don't Lie — But They Can Mislead</h2><p>Statistics on officer-involved shootings, arrests by race, and department spending are real. But they require context to interpret fairly. Cherry-picked numbers in service of a narrative — any narrative — distort the truth.</p><p>On this show, we do context. We do nuance. Because the truth is complicated, and it deserves better than a tweet.</p>",
    coverUrl: "https://picsum.photos/seed/media/800/450",
    status: "published",
    date: "2024-03-01",
    createdAt: NOW - 19 * DAY,
  },
  {
    title: "Understanding Your Second Amendment Rights in 2024",
    excerpt: "Recent Supreme Court rulings have reshaped the legal landscape around gun rights. Here's what every law-abiding citizen needs to know right now.",
    body: "<p>The Second Amendment has been litigated, debated, and distorted more than almost any other part of the Constitution. After the landmark Bruen decision in 2022, the legal landscape shifted significantly — and most Americans don't know what changed.</p><p>Here's what you need to understand.</p><h2>What Bruen Actually Said</h2><p>The Supreme Court ruled 6-3 that the government must justify firearm regulations by pointing to a historical tradition of similar regulation. This raised the bar significantly for states trying to restrict gun ownership.</p><h2>What It Means Practically</h2><p>Several state-level restrictions — particularly around carry permits and certain magazine capacity limits — are now being challenged in lower courts. Some have already been struck down. Others are winding through the appellate system.</p><p>Know your rights. Know your state's laws. And stay informed — because this area of law is moving fast.</p>",
    coverUrl: "https://picsum.photos/seed/secondamendment/800/450",
    status: "published",
    date: "2024-02-23",
    createdAt: NOW - 26 * DAY,
  },
];

// ── Demo Guests ──────────────────────────────────────────────────────────────
const DEMO_GUESTS = [
  {
    name: "Marcus Johnson",
    title: "Former NYPD Detective",
    bio: "20-year veteran of the NYPD, retired detective, and now a leading voice on ethical policing and department reform. Marcus has trained officers across the country and testified before Congress on use-of-force standards.",
    episodeTitle: "The Truth About Police Reform",
    photoUrl: "https://picsum.photos/seed/marcus/200/200",
    createdAt: NOW - 6 * DAY,
  },
  {
    name: "Sarah Williams",
    title: "Army Combat Medic, PTSD Advocate",
    bio: "Sarah served three tours in Afghanistan as an Army combat medic. After returning home, she founded a nonprofit connecting veterans with mental health resources. She's a fierce advocate for expanding VA mental health services.",
    episodeTitle: "Veterans and the Mental Health Crisis",
    photoUrl: "https://picsum.photos/seed/sarah/200/200",
    createdAt: NOW - 13 * DAY,
  },
  {
    name: "Carlos Rivera",
    title: "Border Patrol Agent (Ret.)",
    bio: "Carlos Rivera spent 18 years with US Customs and Border Protection, rising to Senior Agent before retiring in 2022. He now consults on border security policy and is one of the most sought-after voices on immigration enforcement realities.",
    episodeTitle: "Border Security: A Reality Check",
    photoUrl: "https://picsum.photos/seed/carlos/200/200",
    createdAt: NOW - 20 * DAY,
  },
  {
    name: "Patricia Moore",
    title: "Retired Circuit Court Judge",
    bio: "Judge Patricia Moore served 22 years on the bench, presiding over thousands of criminal cases. She is now a vocal critic of mandatory minimum sentencing and advocates for restorative justice programs and judicial reform.",
    episodeTitle: "Inside the Justice System",
    photoUrl: "https://picsum.photos/seed/patricia/200/200",
    createdAt: NOW - 27 * DAY,
  },
  {
    name: "David Harris",
    title: "Police Chief, City of Riverside",
    bio: "Chief Harris took over one of the most troubled police departments in the Southeast and transformed it through community policing initiatives, body camera mandates, and radical transparency. His department is now a national study in trust-building.",
    episodeTitle: "Community Policing: Does It Actually Work?",
    photoUrl: "https://picsum.photos/seed/david/200/200",
    createdAt: NOW - 34 * DAY,
  },
];

// ── Demo Breaking News ───────────────────────────────────────────────────────
const DEMO_BREAKING = [
  { text: "New police body camera legislation advances out of committee — full vote expected next week", createdAt: NOW - 1 * DAY },
  { text: "Veterans benefits expansion bill passes Senate subcommittee with bipartisan support", createdAt: NOW - 2 * DAY },
  { text: "Supreme Court agrees to hear landmark Second Amendment challenge from three states", createdAt: NOW - 3 * DAY },
];

// ── Demo Settings ────────────────────────────────────────────────────────────
const DEMO_ABOUT = {
  name: "Kedrick Scribner",
  title: "Host & Executive Producer",
  bio: "Kedrick Scribner is a former Marine and law enforcement officer turned award-winning talk show host. With over two decades of real-world experience in uniform, Kedrick brings an unfiltered, boots-on-the-ground perspective to the conversations America needs to have.",
  bio2: "Founded on the belief that truth doesn't have a political party, Talk News cuts through the noise to give voice to the experts, veterans, and frontline professionals who actually know what they're talking about. No spin. No agenda. Just truth.",
  yearsExperience: "20+",
  photoUrl: "https://picsum.photos/seed/kedrick/400/500",
  youtube: "https://youtube.com/@talknews",
  twitter: "https://twitter.com/talknews",
  instagram: "https://instagram.com/talknews",
  facebook: "https://facebook.com/talknews",
  _demo: true,
};

const DEMO_LIVE = {
  isLive: false,
  streamUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  chatUrl: "https://www.youtube.com/live_chat?v=dQw4w9WgXcQ",
  nextShowDate: new Date(NOW + 7 * DAY).toISOString().slice(0, 16),
  nextShowTitle: "Election Security: What's Really at Stake",
  _demo: true,
};

// ── Seed Function ────────────────────────────────────────────────────────────
export async function seedDemoData(db) {
  const tag = { _demo: true };

  // Add episodes
  for (const ep of DEMO_EPISODES) {
    await addDoc(collection(db, "episodes"), { ...ep, ...tag });
  }
  // Add blog posts
  for (const post of DEMO_POSTS) {
    await addDoc(collection(db, "blogPosts"), { ...post, ...tag });
  }
  // Add guests
  for (const guest of DEMO_GUESTS) {
    await addDoc(collection(db, "guests"), { ...guest, ...tag });
  }
  // Add breaking news
  for (const item of DEMO_BREAKING) {
    await addDoc(collection(db, "breakingNews"), { ...item, ...tag });
  }
  // Set settings (overwrite)
  await setDoc(doc(db, "settings", "about"), DEMO_ABOUT);
  await setDoc(doc(db, "settings", "live"),  DEMO_LIVE);
}

// ── Clear Function ───────────────────────────────────────────────────────────
const DEMO_COLLECTIONS = ["episodes", "blogPosts", "guests", "breakingNews"];

export async function clearDemoData(db) {
  let deleted = 0;

  // Delete tagged documents from all collections
  for (const col of DEMO_COLLECTIONS) {
    const snap = await getDocs(query(collection(db, col), where("_demo", "==", true)));
    for (const d of snap.docs) {
      await deleteDoc(doc(db, col, d.id));
      deleted++;
    }
  }

  // Clear settings only if they were demo-seeded
  const settingsSnap = await getDocs(collection(db, "settings"));
  for (const d of settingsSnap.docs) {
    if (d.data()._demo) {
      await deleteDoc(doc(db, "settings", d.id));
      deleted++;
    }
  }

  return deleted;
}
